import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Observable, catchError, distinctUntilChanged, map, of } from 'rxjs';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { FormComponent, FormMode } from '@app/shared/forms/components';
import { BankEntity } from '@app/shared/models/bank';
import { BankService, UtilsService } from '@app/shared/services';
import { mapArrayToSelectOptions } from '@app/utils';
import { BankBaseTabPageComponent } from '../bank-base-tab-page.component';
import { BankDetailTabs } from '../bank-detail-tabs.constant';
import { checkSignatureFields } from './bank-check-signature.fields';

@Component({
	selector: 'bank-check-config',
	standalone: true,
	imports: [FormComponent],
	templateUrl: './bank-check-config.component.html',
	styleUrl: './bank-check-config.component.less',
})
export class BankCheckConfigComponent extends BankBaseTabPageComponent implements OnInit {
	@Input() entity: BankEntity;
	@Output() entityChange: EventEmitter<BankEntity> = new EventEmitter<BankEntity>();
	@ViewChild('checkConfigForm') private readonly formComponent: FormComponent;

	private revertEntity: string;
	private readonly destroyRef = inject(DestroyRef);

	form: UntypedFormGroup = new UntypedFormGroup({});
	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: this.readOnly,
			selects: {
				memoLocations: [],
			},
		},
	};
	fields: FormlyFieldConfig[] = [];

	constructor(
		private readonly utilsService: UtilsService,
		private readonly bankService: BankService,
	) {
		super();
	}

	ngOnInit(): void {
		this.revertEntity = JSON.stringify(this.entity);
		this.loadOptions();
	}

	private loadOptions(): void {
		this.utilsService.getCheckMemoLocation().subscribe((locations) => {
			this.options.formState.selects.memoLocations = mapArrayToSelectOptions(locations.checkMemoLocations, {
				value: 'Id',
				label: 'Description',
				all: false,
				sort: 'label',
			});

			this.loadEntityDetail();
		});
	}

	private loadEntityDetail(): void {
		this.fields = this.getFields();
		// NOTE: Some issue with the timing of how the FormComponent is loaded means that the [form] object is not
		// being copied to viewForm, so we need to reference the component directly to access the change detection.
		this.formComponent.viewForm.valueChanges
			.pipe(
				map(() => this.formComponent.viewForm.dirty),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((isDirty) => {
				if (isDirty) {
					this.onChanges();
				} else {
					this.tabChange.emit(null);
				}
			});
	}

	onChanges(): void {
		this.tabChange.emit({
			tabName: BankDetailTabs.CheckConfig,
			cancelChanges: () => this.cancelChanges(),
			saveChanges: () => this.saveChanges(),
			isValid: () => this.formComponent.viewForm.valid,
		});
	}

	cancelChanges(): void {
		this.entity = JSON.parse(this.revertEntity) as BankEntity;
		this.entityChange.emit(this.entity);
		this.formComponent.viewForm.reset(this.entity);
	}

	saveChanges(): Observable<boolean> {
		this.formComponent.viewForm.updateValueAndValidity();

		if (this.formComponent.viewForm.invalid) {
			this.formComponent.viewForm.markAllAsTouched();
			return of(false);
		}

		return this.bankService.updateEntity(this.entity).pipe(
			map((data: BankEntity) => {
				this.entity = data;
				this.entityChange.emit(this.entity);
				this.revertEntity = JSON.stringify(data);
				this.formComponent.viewForm.reset(this.entity);
				return true;
			}),
			catchError(() => of(false)),
		);
	}

	private hasInvalidLocationId(id: number): boolean {
		return !id || id <= 0;
	}

	private getFields(): FormlyFieldConfig[] {
		return [
			{
				wrappers: [FORMLY_FIELD_WRAPPERS.basicWrapper],
				props: {
					class: 'bank-check-config-container',
				},
				fieldGroup: [
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'GLCheckMemo1LocationId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-2',
								props: {
									label: 'Memo 1 Location',
									required: false,
									disabled: this.readOnly,
								},
								expressions: {
									'props.options': 'formState.selects.memoLocations',
								},
							},
							{
								key: 'GLCheckMemo1',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								props: {
									label: 'Memo 1',
									maxLength: 50,
								},
								expressions: {
									'props.disabled': (field: FormlyFieldConfig) =>
										this.readOnly || this.hasInvalidLocationId(field.model.GLCheckMemo1LocationId),
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'GLCheckMemo2LocationId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-2',
								props: {
									label: 'Memo 2 Location',
									required: false,
									disabled: this.readOnly,
								},
								expressions: {
									'props.options': 'formState.selects.memoLocations',
								},
							},
							{
								key: 'GLCheckMemo2',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								props: {
									label: 'Memo 2',
									maxLength: 50,
								},
								expressions: {
									'props.disabled': (field: FormlyFieldConfig) =>
										this.readOnly || this.hasInvalidLocationId(field.model.GLCheckMemo2LocationId),
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'GLCheckMemo3LocationId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-2',
								props: {
									label: 'Memo 3 Location',
									required: false,
									disabled: this.readOnly,
								},
								expressions: {
									'props.options': 'formState.selects.memoLocations',
								},
							},
							{
								key: 'GLCheckMemo3',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-6',
								props: {
									label: 'Memo 3',
									maxLength: 50,
								},
								expressions: {
									'props.disabled': (field: FormlyFieldConfig) =>
										this.readOnly || this.hasInvalidLocationId(field.model.GLCheckMemo3LocationId),
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
								},
							},
						],
					},
					{
						fieldGroupClassName: 'signature-fields-container',
						fieldGroup: [
							checkSignatureFields(1, this.readOnly),
							checkSignatureFields(2, this.readOnly),
							checkSignatureFields(3, this.readOnly),
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'Logo',
								type: FORMLY_FIELD_TYPES.imagePreview,
								className: 'col-xs-6',
								props: {
									labelText: 'Logo',
									isReadOnly: this.readOnly,
									bindToUrl: false,
								},
							},
						],
					},
				],
			},
		];
	}
}
